import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverAnchor } from '@/components/ui/popover'
import { GlobalSearchPanel } from './v2/GlobalSearchPanel'
import { SearchResultsDialog } from './SearchResultsDialog'
import { IconTip } from '@/components/ui/tooltip'

interface GlobalSearchBarProps {
  collapsible?: boolean
}

export function GlobalSearchBar({ collapsible = false }: GlobalSearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [expanded, setExpanded] = useState(!collapsible)
  const [searching, setSearching] = useState(false)

  // Cmd+/ shortcut (Cmd+K is reserved for creating a candidate)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault()
        setExpanded(true)
        // Defer focus until input renders
        requestAnimationFrame(() => {
          inputRef.current?.focus()
          setIsOpen(true)
        })
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  // Collapse on outside click when empty
  useEffect(() => {
    if (!collapsible || !expanded) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (wrapperRef.current?.contains(target)) return
      // Ignore clicks inside the popover content
      const popoverContent = document.querySelector('[data-radix-popper-content-wrapper]')
      if (popoverContent?.contains(target)) return
      if (!query) {
        setExpanded(false)
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [collapsible, expanded, query])

  const handleOpenCandidate = useCallback((id: string) => {
    setIsOpen(false)
    navigate(`/candidates/${id}`)
  }, [navigate])

  const handleExpand = () => {
    setExpanded(true)
    requestAnimationFrame(() => {
      inputRef.current?.focus()
      setIsOpen(true)
    })
  }

  const handleCollapse = () => {
    setQuery('')
    setIsOpen(false)
    setExpanded(false)
  }

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverAnchor asChild>
          <div
            ref={wrapperRef}
            className={cn(
              'relative hidden md:flex items-center justify-end',
              collapsible ? (expanded ? 'h-8 w-8' : 'h-8') : '',
            )}
          >
            {collapsible && (
              <button
                type="button"
                onClick={expanded ? handleCollapse : handleExpand}
                aria-label={expanded ? 'Close search' : 'Search'}
                className={cn(
                  'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-virgilio-purple/40',
                  expanded
                    ? 'relative z-[1] h-8 w-8 rounded-full flex items-center justify-center hover:bg-white/[0.08]'
                    : 'hidden md:flex h-[30px] min-w-[180px] shrink items-center gap-2 rounded-lg border border-white/[0.12] bg-white/[0.08] px-2.5 text-[13px] text-white/70 hover:bg-white/[0.12] hover:text-white',
                )}
              >
                {expanded ? (
                  <X className="h-4 w-4" />
                ) : (
                  <>
                    <Search className="h-3.5 w-3.5 shrink-0" />
                    <span className="min-w-0 truncate whitespace-nowrap">
                      Search candidates, jobs, companies…
                    </span>
                    <span className="ml-auto flex items-center gap-0.5 text-[10px] text-white/50 shrink-0">
                      <kbd className="px-1 py-0.5 rounded font-mono bg-white/[0.12]">⌘</kbd>
                      <kbd className="px-1 py-0.5 rounded font-mono bg-white/[0.12]">/</kbd>
                    </span>
                  </>
                )}
              </button>
            )}
            {(!collapsible || expanded) && (
              <div
                className={cn(
                  collapsible
                    ? 'absolute right-9 top-1/2 -translate-y-1/2 z-[1]'
                    : 'relative',
                )}
              >
                {/* §12: while results load, the search icon becomes a 13px inline spinner. */}
                <span className="absolute left-3 top-1/2 -translate-y-1/2 flex h-4 w-4 items-center justify-center text-virgilio-muted pointer-events-none" aria-hidden="true">
                  {searching ? (
                    <span className="gio-spinner gio-spinner-current" style={{ width: 13, height: 13 }} />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </span>
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setIsOpen(true) }}
                  onFocus={() => setIsOpen(true)}
                  placeholder="Search candidates, jobs, companies…"
                  className={cn(
                    'h-9 rounded-lg border border-virgilio-border pl-9 text-sm font-poppins',
                    'placeholder:text-virgilio-muted transition-all duration-200',
                    'focus:outline-none focus:ring-2 focus:ring-virgilio-purple/30 focus:border-virgilio-purple/50',
                    'hover:border-virgilio-purple/40',
                    collapsible
                      ? 'w-[320px] pr-9 bg-[#0d0d09] shadow-[0_8px_24px_-8px_rgba(0,0,0,0.6)]'
                      : 'w-[320px] focus:w-[420px] pr-12 bg-surface-primary',
                  )}
                />
                {/* §12: the clear button fades in (120ms) only when there's text. */}
                <IconTip label="Clear search">
                  <button
                    type="button"
                    aria-label="Clear search"
                    tabIndex={query ? 0 : -1}
                    aria-hidden={!query || undefined}
                    data-visible={query ? '' : undefined}
                    // Keep focus in the input so the results panel stays open (and keeps its scope).
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { setQuery(''); inputRef.current?.focus() }}
                    className={cn(
                      'gio-search-clear absolute right-2 top-1/2 -translate-y-1/2 z-[1] flex h-6 w-6 items-center justify-center rounded-md',
                      collapsible ? 'text-white/60 hover:text-white' : 'text-virgilio-muted hover:text-foreground',
                    )}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </IconTip>
                {!collapsible && !query && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5 text-[10px] text-virgilio-muted pointer-events-none">
                    <kbd className="px-1.5 py-0.5 bg-virgilio-border/50 rounded font-mono">⌘</kbd>
                    <kbd className="px-1 py-0.5 bg-virgilio-border/50 rounded font-mono">/</kbd>
                  </div>
                )}
              </div>
            )}
          </div>
        </PopoverAnchor>

        <PopoverContent
          align="start"
          sideOffset={10}
          // Search results never animate (§2 command palette, §12 search).
          className="p-0 border-0 bg-transparent shadow-none w-auto gio-static"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          // The input and its clear button sit outside the panel; using them keeps it open.
          onInteractOutside={(e) => {
            if (wrapperRef.current?.contains(e.target as Node)) e.preventDefault()
          }}
        >
          <GlobalSearchPanel
            query={query}
            onQueryChange={(q) => { setQuery(q); inputRef.current?.focus() }}
            onClose={() => setIsOpen(false)}
            onOpenCandidate={handleOpenCandidate}
            onLoadingChange={setSearching}
          />
        </PopoverContent>
      </Popover>

      {/* Mobile fallback */}
      <button
        onClick={() => setIsDialogOpen(true)}
        className="md:hidden h-9 w-9 rounded-lg border border-virgilio-border bg-surface-primary flex items-center justify-center hover:bg-virgilio-purple/5 transition-colors"
        aria-label="Search"
      >
        <Search className="h-4 w-4 text-virgilio-muted" />
      </button>

      <SearchResultsDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        initialQuery={query}
      />
    </>
  )
}
